import { useState, useEffect } from 'react';
import { Plus, Trash, PencilSimple, Image, X, FloppyDisk } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import api from '@/lib/api';

const EMPTY = { title: '', content: '', date: '', image_url: '' };

export default function CMSNewsPanel({ token }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchItems = async () => {
    try { const res = await api.get('/cms/news'); setItems(res.data); }
    catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchItems(); }, []);

  const handleSave = async () => {
    try {
      if (editing) await api.put(`/admin/cms/news/${editing}?token=${token}`, form);
      else await api.post(`/admin/cms/news?token=${token}`, form);
      setEditing(null); setForm(EMPTY); fetchItems();
    } catch (e) { alert('Failed to save'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this news article?')) return;
    try { await api.delete(`/admin/cms/news/${id}?token=${token}`); fetchItems(); }
    catch (e) { alert('Failed to delete'); }
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post(`/admin/upload?token=${token}`, formData);
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      setForm(prev => ({ ...prev, image_url: `${API_URL}/api/files/${res.data.path}` }));
    } catch (e) { alert('Upload failed'); }
    finally { setUploading(false); }
  };

  if (loading) return <div className="text-center py-12 text-[#4A5568]">Loading...</div>;

  return (
    <div className="space-y-6" data-testid="cms-news-panel">
      <div className="bg-white border border-slate-200 p-6">
        <h3 className="text-lg font-bold text-[#0A1F3D] mb-4">{editing ? 'Edit Article' : 'Add News Article'}</h3>
        <div className="grid md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-xs font-medium text-[#4A5568] mb-1">Title *</label>
            <Input data-testid="news-title-input" value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} placeholder="Article title" className="rounded-none" />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#4A5568] mb-1">Date</label>
            <Input data-testid="news-date-input" value={form.date} onChange={(e) => setForm({...form, date: e.target.value})} placeholder="e.g. March 2024" className="rounded-none" />
          </div>
        </div>
        <div className="mb-4">
          <label className="block text-xs font-medium text-[#4A5568] mb-1">Content</label>
          <Textarea data-testid="news-content-input" value={form.content} onChange={(e) => setForm({...form, content: e.target.value})} rows={4} placeholder="Article content..." className="rounded-none" />
        </div>
        <div className="mb-4">
          <label className="block text-xs font-medium text-[#4A5568] mb-1">Image</label>
          <div className="flex gap-2">
            <Input value={form.image_url} onChange={(e) => setForm({...form, image_url: e.target.value})} placeholder="Image URL or upload" className="rounded-none flex-1" />
            <label className="cursor-pointer">
              <input type="file" accept="image/*" onChange={handleUpload} className="hidden" />
              <Button type="button" variant="outline" className="rounded-none" disabled={uploading}>
                <Image size={16} /> {uploading ? '...' : 'Upload'}
              </Button>
            </label>
          </div>
        </div>
        <div className="flex gap-2">
          <Button data-testid="news-save-btn" onClick={handleSave} disabled={!form.title} className="bg-[#D4AF37] text-[#0A1F3D] hover:bg-[#c5a033] rounded-none">
            <FloppyDisk size={16} /> {editing ? 'Update' : 'Add Article'}
          </Button>
          {editing && <Button variant="outline" onClick={() => { setEditing(null); setForm(EMPTY); }} className="rounded-none"><X size={16} /> Cancel</Button>}
        </div>
      </div>

      <div className="bg-white border border-slate-200">
        <div className="p-4 border-b border-slate-200"><h3 className="font-bold text-[#0A1F3D]">{items.length} Articles</h3></div>
        {items.length === 0 ? (
          <div className="text-center py-8 text-[#4A5568]">No news articles yet.</div>
        ) : (
          <div className="divide-y divide-slate-100">
            {items.map((item) => (
              <div key={item.id} className="flex items-center gap-4 p-4 hover:bg-[#F5F5F5]/50">
                {item.image_url && <img src={item.image_url} alt="" className="w-16 h-12 object-cover flex-shrink-0 border" />}
                <div className="flex-1 min-w-0">
                  <h4 className="font-medium text-[#0A1F3D] text-sm truncate">{item.title}</h4>
                  <p className="text-xs text-[#4A5568] truncate">{item.content}</p>
                  <span className="text-xs text-[#D4AF37]">{item.date}</span>
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  <button onClick={() => { setEditing(item.id); setForm({ title: item.title, content: item.content, date: item.date, image_url: item.image_url }); }} className="p-2 hover:bg-slate-100 text-[#4A5568] hover:text-[#0A1F3D]"><PencilSimple size={16} /></button>
                  <button onClick={() => handleDelete(item.id)} className="p-2 hover:bg-red-50 text-[#4A5568] hover:text-red-500"><Trash size={16} /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
